import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Icon, type IconName } from "./Icon";
import { Button, cx } from "./ui";

export type MoreItem = { label: string; icon?: IconName; onSelect: () => void; danger?: boolean; disabled?: boolean };

/** A "⋯" button with the less frequent actions, so the header keeps only the next step visible. */
export function MoreMenu({ items, label }: { items: MoreItem[]; label?: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    // Start on the first item, so the arrow keys work right away.
    ref.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus();
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (!items.length) return null;

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      ref.current?.querySelector<HTMLButtonElement>("[aria-haspopup]")?.focus();
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const all = [...(ref.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? [])];
    const i = all.indexOf(document.activeElement as HTMLButtonElement);
    all[(i + (e.key === "ArrowDown" ? 1 : -1) + all.length) % all.length]?.focus();
  };

  return (
    <div ref={ref} className="relative" onKeyDown={onKey}>
      <Button
        icon="more"
        aria-label={label ?? t("common.more")}
        title={label ?? t("common.more")}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((o) => !o)}
      />
      {open && (
        <div
          id={menuId}
          role="menu"
          className="absolute top-full right-0 z-30 mt-1 flex min-w-52 flex-col rounded-[var(--radius-card)] border border-border bg-surface p-1 shadow-[var(--shadow)]"
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              className={cx(
                "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm hover:bg-surface-2 focus:bg-surface-2 focus:outline-none disabled:opacity-50",
                item.danger ? "text-fail-fg" : "text-text",
              )}
            >
              {item.icon && <Icon name={item.icon} className={item.danger ? undefined : "text-muted"} />}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
